import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Star, Gift } from "lucide-react";
import { Card, field, cfield, btn } from "./ui";
import { fetchContent } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoyaltyTab = ({ auth }) => {
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({ threshold: 10, reward: "$50 off your next project" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pointsDraft, setPointsDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/loyalty`, auth).then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => {
    load();
    fetchContent(true).then((c) => {
      if (c?.loyalty) setSettings({ threshold: c.loyalty.threshold ?? 10, reward: c.loyalty.reward ?? "" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const c = await fetchContent(true);
      await axios.put(`${API}/admin/content`, { ...c, loyalty: settings }, auth);
      toast.success("Loyalty settings saved.");
    } catch { toast.error("Save failed."); }
    finally { setBusy(false); }
  };

  const addCustomer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/loyalty/customers`, { name, email }, auth);
      toast.success("Customer added.");
      setName(""); setEmail("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add customer."); }
  };

  const applyPoints = async (id, delta) => {
    const d = pointsDraft[id] || {};
    const amount = delta ?? parseInt(d.amount || "0", 10);
    if (!amount) { toast.error("Enter points first."); return; }
    try {
      await axios.post(`${API}/admin/loyalty/customers/${id}/points`, { delta: amount, reason: d.reason || "" }, auth);
      setPointsDraft((s) => ({ ...s, [id]: {} }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed."); }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/admin/loyalty/customers/${id}`, auth);
      setCustomers((s) => s.filter((c) => c.id !== id));
    } catch { toast.error("Delete failed."); }
  };

  return (
    <div className="space-y-8" data-testid="loyalty-tab">
      <Card title="Reward Settings" color="bg-staxyellow" testid="loyalty-settings-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink/50">Points to earn reward</label>
            <input data-testid="loyalty-threshold" type="number" min="1" value={settings.threshold}
              onChange={(e) => setSettings((s) => ({ ...s, threshold: parseInt(e.target.value) || 1 }))} className={field} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink/50">Reward they earn</label>
            <input data-testid="loyalty-reward" value={settings.reward}
              onChange={(e) => setSettings((s) => ({ ...s, reward: e.target.value }))} className={field} />
          </div>
        </div>
        <button data-testid="loyalty-settings-save" onClick={saveSettings} disabled={busy} className={`${btn} mt-3 bg-ink text-white`}>
          {busy ? "Saving..." : "Save Settings"}
        </button>
      </Card>

      <Card title="Add Customer" color="bg-staxcyan" testid="loyalty-add-card">
        <form onSubmit={addCustomer} className="flex flex-col sm:flex-row gap-3">
          <input data-testid="loyalty-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" className={field} />
          <input data-testid="loyalty-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
          <button data-testid="loyalty-add" type="submit" className={`${btn} bg-staxcyan text-ink shrink-0`}><Plus size={15} /> Add</button>
        </form>
      </Card>

      <Card title={`Customers (${customers.length})`} color="bg-staxmagenta" testid="loyalty-list">
        {customers.length === 0 && <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">No loyalty customers yet.</p>}
        <div className="space-y-3">
          {customers.map((c) => {
            const d = pointsDraft[c.id] || {};
            const canRedeem = c.points >= settings.threshold;
            return (
              <div key={c.id} data-testid={`loyalty-customer-${c.id}`} className="border-2 border-ink/15 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display uppercase text-lg leading-tight">{c.name}</p>
                    <p className="text-sm text-ink/60">{c.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span data-testid={`loyalty-points-${c.id}`} className="flex items-center gap-1.5 border-2 border-ink bg-staxyellow px-3 py-1.5 font-display text-xl">
                      <Star size={15} /> {c.points}
                    </span>
                    {canRedeem && (
                      <button data-testid={`loyalty-redeem-${c.id}`} onClick={() => applyPoints(c.id, -Math.abs(settings.threshold))}
                        className={`${btn} bg-staxmagenta text-white`}>
                        <Gift size={14} /> Redeem
                      </button>
                    )}
                    <button data-testid={`loyalty-delete-${c.id}`} onClick={() => del(c.id)} aria-label="Delete customer"
                      className="border-2 border-ink p-1.5 hover:bg-staxmagenta hover:text-white transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <input data-testid={`loyalty-delta-${c.id}`} type="number" value={d.amount ?? ""}
                    onChange={(e) => setPointsDraft((s) => ({ ...s, [c.id]: { ...d, amount: e.target.value } }))}
                    placeholder="+/- points" className={`${cfield} sm:w-32`} />
                  <input data-testid={`loyalty-reason-${c.id}`} value={d.reason ?? ""}
                    onChange={(e) => setPointsDraft((s) => ({ ...s, [c.id]: { ...d, reason: e.target.value } }))}
                    placeholder="Reason (e.g. Logo project)" className={cfield} />
                  <button data-testid={`loyalty-apply-${c.id}`} onClick={() => applyPoints(c.id)} className={`${btn} bg-staxcyan text-ink shrink-0`}>
                    Apply
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};
